import React, { useEffect, useState } from "react";

import useUrlParams from "../customHooks/useUrlParams";

import { useLazyQuery } from "@apollo/client";
import { responseType } from "../utils/formTypes";
import {
  EMPTY_STRING,
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
} from "../utils/constants";
import { OrderDirection } from "../utils/commonQueryTypes";
import { getModifiedScrollHeight } from "../utils/UtilFunctions";
import {
  FormAutocomplete,
  LabeledAutocomplete,
  formClasses,
  labelClasses,
} from "../styles/AutocompleteStyles";
import { TextField } from "@mui/material";
import {
  AtsSubsidiarySiteEdge,
  AtsSubsidiarySiteOrderField,
  GetSubsidiarySitesResult,
  GetSubsidiarySitesVar,
} from "../components/modules/asset/masters/subsidary/queries/types";
import { GetSubsidiarySites } from "../components/modules/asset/masters/subsidary/queries/queries";
import useLoggedInUserDetails from "../customHooks/useLoggedInUserDetails";

interface Props {
  onKeyDown:
    | ((
        event: React.KeyboardEvent<HTMLDivElement> & {
          defaultMuiPrevented?: boolean;
        }
      ) => void)
    | undefined;
  onChange: (newValue: responseType) => void;
  value: number;
  label: string;
  companyId?: number;
  subsidiaryId?: number;
  placeholder?:string;
}

const SiteSelect = ({
  onChange,
  onKeyDown,
  value,
  label,
  companyId,
  subsidiaryId,
  placeholder
}: Props) => {

  const { customer_id } = useUrlParams();
  const [searchData, setSearchData] = useState("");
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [categories, setCategories] = useState<AtsSubsidiarySiteEdge[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<responseType[]>([]);
  const [autoValue, setAutoValue] = useState<responseType | null>(null);
  const { user_details } = useLoggedInUserDetails();
  const [getSubsidiarySites, { data, loading, fetchMore }] = useLazyQuery<
    GetSubsidiarySitesResult,
    GetSubsidiarySitesVar
  >(GetSubsidiarySites);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetSubsidiarySites.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = categories.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setCategories(updatedNewData);
        setCategoryOptions(
          updatedNewData.map(({ node }) => ({
            label: node.name,
            value: node.id,
          }))
        );
      } else {
        setCategories(newData);
        setCategoryOptions(
          newData.map(({ node }) => ({
            label: node.name,
            value: node.id,
          }))
        );
      }
      setEndCursor(data.GetSubsidiarySites.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        getSubsidiarySites({
          variables: {
           
            after: null,
            customerID: customer_id ? customer_id : 0,
            companyID: companyId ? companyId : 0,
            empID: user_details.id,
            first: ROWS_PER_PAGE,
            orderBy: {
              direction: OrderDirection.ASC,
              field: AtsSubsidiarySiteOrderField.NAME,
            },
            name: EMPTY_STRING,
            subsidiaryID: subsidiaryId ? subsidiaryId : 0,
          },
        });
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, [
    customer_id,
    getSubsidiarySites,
    user_details.id,
    searchData,
    companyId,
    subsidiaryId,
  ]);

  useEffect(() => {
    if (value) {
      const foundValue = categoryOptions.find(
        ({ value: optionsValue }) => Number(optionsValue) === Number(value)
      );

      setAutoValue(foundValue ? foundValue : null);
    } else {
      setAutoValue(null);
    }
  }, [value, data, categoryOptions]);

  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prevResult;

            const newEdges = fetchMoreResult.GetSubsidiarySites.edges;
            const pageInfo = fetchMoreResult.GetSubsidiarySites.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck = prevResult.GetSubsidiarySites.edges.filter(
              ({ node: { id } }) =>
                newEdges.findIndex(
                  ({ node: { id: newId } }) => newId === id
                ) !== -1
            );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetSubsidiarySites: {
                edges: [...categories, ...newEdges],
                pageInfo,
                totalCount: data ? data.GetSubsidiarySites.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };

  let commonAutocompleteClassName = label
    ? labelClasses.inputRoot
    : formClasses.inputRoot;
  let commonTextFieldClassName = label
    ? labelClasses.formControlRoot
    : formClasses.formControlRoot;

  const Autocomplete =
    label.length > 0 ? LabeledAutocomplete : FormAutocomplete;

  return (
    <Autocomplete
      className={commonAutocompleteClassName}
      options={categoryOptions}
      //   onKeyDown={(e) => {
      //     if (e.key === Keys.ENTER) {
      //       e.preventDefault();
      //       handleMUISelectEvent(e);
      //     }
      //   }}
      value={autoValue}
      openOnFocus
      forcePopupIcon
      slotProps={{
        listbox: {
          onScroll: handleScroll,
        },
      }}
      onChange={(e, newValue) => {
        onChange(newValue as responseType);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          onChange={(e) => setSearchData(e.target.value)}
          className={commonTextFieldClassName}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          placeholder={placeholder}
        />
      )}
    />
  );
};

export default SiteSelect;
